import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Assessment, GetApp, Add } from '@mui/icons-material';
import dayjs from '../utils/dayjs';
import toast from 'react-hot-toast';
import { reports } from '../services/api';

export default function ReportsPage() {
  const [reportList, setReportList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ type: 'CUSTOM', tradeMode: 'BOTH', fromDate: '', toDate: '', title: '' });

  const load = async () => {
    setLoading(true);
    try { const res = await reports.list(); setReportList(res.data.data); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    try {
      await reports.create(form);
      toast.success('Report generation started!');
      setFormOpen(false);
      setTimeout(load, 3000);
    } catch { /* handled */ }
  };

  const statusColor = (s) => ({ COMPLETED: 'success', GENERATING: 'warning', FAILED: 'error', PENDING: 'default' }[s] || 'default');

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Reports</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>Generate Report</Button>
      </Box>

      <Card sx={{ borderRadius: 2 }}>
        {loading ? <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: '#f5f5f5' } }}>
                  <TableCell>ID</TableCell><TableCell>Title</TableCell><TableCell>Type</TableCell>
                  <TableCell>Mode</TableCell><TableCell>Period</TableCell><TableCell align="right">Trades</TableCell>
                  <TableCell align="right">Net P&L</TableCell><TableCell align="right">Win Rate</TableCell>
                  <TableCell>Status</TableCell><TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportList.length === 0 ? (
                  <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4 }}>No reports yet. Click "Generate Report".</TableCell></TableRow>
                ) : reportList.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>#{r.id}</TableCell>
                    <TableCell><Typography variant="body2" fontWeight={500}>{r.title}</Typography></TableCell>
                    <TableCell><Chip size="small" label={r.report_type} /></TableCell>
                    <TableCell><Chip size="small" label={r.trade_mode} color="primary" /></TableCell>
                    <TableCell>{dayjs(r.from_date).format('DD/MM/YY')} - {dayjs(r.to_date).format('DD/MM/YY')}</TableCell>
                    <TableCell align="right">{r.total_trades}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(r.net_pnl) >= 0 ? '#2e7d32' : '#c62828', fontWeight: 700 }}>
                      {r.net_pnl ? `₹${parseFloat(r.net_pnl).toFixed(0)}` : '—'}
                    </TableCell>
                    <TableCell align="right">{r.win_rate ? `${parseFloat(r.win_rate).toFixed(1)}%` : '—'}</TableCell>
                    <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                    <TableCell>
                      {r.status === 'COMPLETED' && <>
                        <Button size="small" startIcon={<GetApp />} onClick={() => reports.download(r.id, 'pdf')}>PDF</Button>
                        <Button size="small" startIcon={<GetApp />} onClick={() => reports.download(r.id, 'excel')}>Excel</Button>
                      </>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select value={form.type} label="Report Type" onChange={e => setForm({ ...form, type: e.target.value })}>
                  <MenuItem value="DAILY">Daily</MenuItem><MenuItem value="WEEKLY">Weekly</MenuItem>
                  <MenuItem value="MONTHLY">Monthly</MenuItem><MenuItem value="CUSTOM">Custom</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Trade Mode</InputLabel>
                <Select value={form.tradeMode} label="Trade Mode" onChange={e => setForm({ ...form, tradeMode: e.target.value })}>
                  <MenuItem value="BOTH">Both</MenuItem><MenuItem value="PAPER">Paper</MenuItem><MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="From Date" InputLabelProps={{ shrink: true }} value={form.fromDate} onChange={e => setForm({ ...form, fromDate: e.target.value })} /></Grid>
            <Grid item xs={6}><TextField fullWidth type="date" label="To Date" InputLabelProps={{ shrink: true }} value={form.toDate} onChange={e => setForm({ ...form, toDate: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Title (optional)" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!form.fromDate || !form.toDate} startIcon={<Assessment />}>Generate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
